import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Text,
  Label,
  Spinner,
  MessageBar,
  MessageBarBody,
  Dropdown,
  Option,
  OptionGroup,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular, OpenRegular } from '@fluentui/react-icons';
import { apiFetch } from '../lib/apiFetch';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: '24px',
    ...shorthands.padding('24px'),
    alignItems: 'stretch',
    height: 'calc(100vh - 120px)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '320px',
    flexShrink: 0,
    ...shorthands.padding('20px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow4,
    overflowY: 'auto',
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  divider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    marginTop: '4px',
    marginBottom: '4px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
  },
  previewPane: {
    flex: 1,
    minWidth: '420px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  frame: {
    width: '100%',
    flex: 1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground3,
  },
});

type Fixture = { id: string; label: string; note?: string };

const INVOICE_FIXTURES: Fixture[] = [
  { id: 'realistic', label: 'Realistic invoice', note: '5 lines, deposit + balance' },
  { id: 'branded', label: 'Branded sample (#9)', note: 'mirrors docs/sample-invoice.pdf' },
  { id: '40lines', label: '40-line invoice', note: 'pagination / header repeat' },
  { id: 'paid', label: 'Fully paid', note: 'PAID stamp' },
  { id: 'zero', label: 'Zero-tax', note: 'no subtotal rows' },
  { id: 'multi', label: 'Multi-tax', note: 'GST + QST lines' },
  { id: 'minimal', label: 'Minimal', note: 'all optionals absent' },
];

const QUOTE_FIXTURES: Fixture[] = [
  { id: 'quote', label: 'Realistic quote', note: 'Valid-through, no Paid/Due' },
];

type DocType = 'invoice' | 'quote';
type LiveDoc = { id: string; type: DocType; number: string | null; customer: string };

const TYPE_LABEL: Record<DocType, string> = { invoice: 'Invoice', quote: 'Quote' };

// Option label, prefixed by document type per request.
const liveLabel = (d: LiveDoc) =>
  `${TYPE_LABEL[d.type]}: #${d.number ?? d.id.slice(0, 8)}${d.customer ? ` — ${d.customer}` : ''}`;

export default function PdfTemplatePreview() {
  const styles = useStyles();
  const [mode, setMode] = useState<'fixture' | 'live'>('fixture');
  const [fixture, setFixture] = useState('realistic');
  const [liveDocs, setLiveDocs] = useState<LiveDoc[]>([]);
  const [liveDocId, setLiveDocId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');
  // Track the live object URL so we can revoke it when it's replaced/unmounted.
  const urlRef = useRef('');

  // Fetch the live invoice + quote lists once for the document dropdown.
  useEffect(() => {
    (async () => {
      try {
        const [inv, quo] = await Promise.all([
          apiFetch('/booqable/documents?document_type=invoice').then((r) => r.json()),
          apiFetch('/booqable/documents?document_type=quote').then((r) => r.json()),
        ]);
        const map = (data: any, type: DocType): LiveDoc[] =>
          (data?.data || []).map((it: any) => ({
            id: it.id,
            type,
            number: it.attributes?.number ?? null,
            customer: it.attributes?.customer_name ?? '',
          }));
        setLiveDocs([...map(inv, 'invoice'), ...map(quo, 'quote')]);
      } catch {
        /* live list is optional — fixtures still work */
      }
    })();
  }, []);

  // The URL whose bytes are currently shown — fixture route or live-doc route.
  const currentUrl = useMemo(
    () =>
      mode === 'live' && liveDocId
        ? `/documents/${liveDocId}/pdf?renderer=react`
        : `/dev/pdf-preview?fixture=${encodeURIComponent(fixture)}`,
    [mode, liveDocId, fixture]
  );

  const renderUrl = useCallback(async (url: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(url);
      if (!res.ok) {
        let message = `Render failed (${res.status})`;
        try {
          const data = await res.json();
          if (data?.error) message = data.error;
        } catch {
          /* non-JSON error body */
        }
        throw new Error(message);
      }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = objUrl;
      setPdfUrl(objUrl);
    } catch (e: any) {
      setError(e.message || 'Render failed');
    } finally {
      setBusy(false);
    }
  }, []);

  // Render whenever the active selection changes (and on mount).
  useEffect(() => {
    renderUrl(currentUrl);
  }, [currentUrl, renderUrl]);

  // Revoke the last object URL on unmount.
  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const allFixtures = [...INVOICE_FIXTURES, ...QUOTE_FIXTURES];
  const selectedFixture = allFixtures.find((f) => f.id === fixture);
  const selectedLive = liveDocs.find((d) => d.id === liveDocId);

  const fixtureOptions = (fixtures: Fixture[]) =>
    fixtures.map((fx) => (
      <Option key={fx.id} value={fx.id} text={fx.label}>
        {fx.label}
      </Option>
    ));

  const liveOptions = (type: DocType) =>
    liveDocs
      .filter((d) => d.type === type)
      .map((d) => (
        <Option key={d.id} value={d.id} text={liveLabel(d)}>
          {liveLabel(d)}
        </Option>
      ));

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <Text size={500} weight="semibold">PDF template preview</Text>
        <Text className={styles.hint}>
          Render the invoice / quote react-pdf templates from sample fixtures, or
          pick a real document to render live. Edit a template and hit Refresh.
        </Text>

        <div className={styles.field}>
          <Label htmlFor="pdf-fixture">Sample fixture</Label>
          <Dropdown
            id="pdf-fixture"
            size="large"
            value={mode === 'fixture' ? selectedFixture?.label ?? '' : ''}
            selectedOptions={mode === 'fixture' && fixture ? [fixture] : []}
            placeholder="Select a fixture…"
            onOptionSelect={(_, d) => {
              if (!d.optionValue) return;
              setMode('fixture');
              setFixture(d.optionValue);
            }}
          >
            <OptionGroup label="Invoice">{fixtureOptions(INVOICE_FIXTURES)}</OptionGroup>
            <OptionGroup label="Quote">{fixtureOptions(QUOTE_FIXTURES)}</OptionGroup>
          </Dropdown>
          {mode === 'fixture' && selectedFixture?.note ? (
            <Text className={styles.hint}>{selectedFixture.note}</Text>
          ) : null}
        </div>

        <div className={styles.divider} />

        <div className={styles.field}>
          <Label htmlFor="pdf-live">Live document</Label>
          <Dropdown
            id="pdf-live"
            size="large"
            value={mode === 'live' && selectedLive ? liveLabel(selectedLive) : ''}
            selectedOptions={mode === 'live' && liveDocId ? [liveDocId] : []}
            placeholder={liveDocs.length ? 'Select a document…' : 'Loading documents…'}
            onOptionSelect={(_, d) => {
              if (!d.optionValue) return;
              setMode('live');
              setLiveDocId(d.optionValue);
            }}
          >
            <OptionGroup label="Invoices">{liveOptions('invoice')}</OptionGroup>
            <OptionGroup label="Quotes">{liveOptions('quote')}</OptionGroup>
          </Dropdown>
        </div>

        <div className={styles.actions}>
          <Button
            appearance="primary"
            icon={busy ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
            disabled={busy}
            onClick={() => renderUrl(currentUrl)}
          >
            Refresh
          </Button>
          <Button
            icon={<OpenRegular />}
            disabled={!pdfUrl}
            onClick={() => pdfUrl && window.open(pdfUrl, '_blank', 'noopener')}
          >
            Open in new tab
          </Button>
        </div>

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
      </div>

      <div className={styles.previewPane}>
        <Text size={400} weight="semibold">Preview</Text>
        {pdfUrl ? (
          <iframe className={styles.frame} title="PDF preview" src={pdfUrl} />
        ) : (
          <div className={styles.frame} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Text className={styles.hint}>{busy ? 'Rendering…' : 'Select a fixture or document to render.'}</Text>
          </div>
        )}
      </div>
    </div>
  );
}
